<template>
    <div
        id="community-profiles-viz"
        class="app"
        @click="() => {}"
    >
        <splash-screen
            v-if="!ready"
            :region-model="appModel.regionModel"
        />
        <!-- If we need a UI to let user enter filename, other options, this needs to be placed somewhere visible -->
        <DownloadPDF :model="appModel.pdfModel" />
        <div
            v-if="ready"
            class="app-content"
        >
            <modal
                v-if="appModel.linksModel.link === 'howToRead'"
                :value="!!appModel.linksModel.link"
                @close="() => {
                    appModel.linksModel.link = null;
                    closeModal();
                }"
            >
                <how-to-read-page :content="appModel.linksModel.linkContent" />
            </modal>
            <modal
                v-else-if="appModel.linksModel.link === 'faq'"
                :value="!!appModel.linksModel.link"
                @close="() => {
                    appModel.linksModel.link = null;
                    closeModal();
                }"
            >
                <faq-page :content="appModel.linksModel.linkContent" />
            </modal>
            <modal
                v-if="$screens.mobile"
                :value="!!appModel.mobileNavModel.selected"
                class="mobile-nav"
                @close="() => {
                    appModel.mobileNavModel.selected = null;
                    closeModal();
                }"
            >
                <div
                    v-if="appModel.mobileNavModel.selected === 'menu'"
                >
                    <sidebar
                        :controls-model="appModel.controlsModel"
                        :region-model="appModel.regionModel"
                        :pages-model="appModel.pagesModel"
                        :links-model="appModel.linksModel"
                        :map-model="appModel.mapModel"
                    />
                </div>
                <div
                    v-if="appModel.mobileNavModel.selected === 'find'"
                >
                    <location-search
                        :region-model="appModel.regionModel"
                    />
                </div>
            </modal>
            <sidebar
                v-else
                :region-model="appModel.regionModel"
                :pages-model="appModel.pagesModel"
                :links-model="appModel.linksModel"
                :map-model="appModel.mapModel"
            />

            <div
                class="body-container"
            >
                <top-panel
                    :region-model="appModel.regionModel"
                    :mobile-nav-model="appModel.mobileNavModel"
                    :controls-model="appModel.controlsModel"
                />
                <dynamic-resize-container v-if="$screens.mobile">
                    <template #default="{width}">
                        <lga-map
                            v-if="appModel.mapModel"
                            :width="width"
                            :height="240"
                            class="flex-grow flex-shrink"
                            :model="appModel.mapModel"
                        />
                    </template>
                </dynamic-resize-container>
                <div
                    ref="enterViewContainer"
                    class="scroll-container"
                >
                    <component
                        :is="page.type"
                        v-for="page in appModel.pagesModel.pages"
                        :key="page.id"
                        :model="page.model"
                        :pages-model="appModel.pagesModel"
                        :content="page"
                        @resize="onPageResize"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import enterView from 'enter-view';

import appModel from '@/model/AppModel';
import dataService from '@/services/DataService';

import tailwindConfig from '@/../tailwind.config';
import './assets/tailwind.css';
import './assets/ie.css';

import SplashScreen from '@/views/SplashScreen.vue';
import Sidebar from '@/views/Sidebar.vue';
import TopPanel from '@/views/TopPanel.vue';
import GenericTab from '@/views/tabs/GenericTab.vue';
import ConclusionTab from '@/views/tabs/ConclusionTab.vue';
import HowToReadPage from '@/views/HowToReadPage.vue';
import FaqPage from '@/views/FaqPage.vue';
import ScrollyPage from '@/views/tabs/ScrollyPage.vue';
import Modal from '@/views/Modal.vue';
import LocationSearch from '@/views/LocationSearch.vue';
import DownloadPDF from '@/views/DownloadPDF.vue';
import LgaMap from '@/components/LgaMap.vue';
import DynamicResizeContainer from '@/components/DynamicResizeContainer.vue';

window.dha_appModel = appModel;

export default {
    name: 'App',
    components: {
        Sidebar,
        TopPanel,
        GenericTab,
        SplashScreen,
        ConclusionTab,
        HowToReadPage,
        FaqPage,
        ScrollyPage,
        Modal,
        LocationSearch,
        DownloadPDF,
        LgaMap,
        DynamicResizeContainer
    },
    data() {
        return {
            loaded: false,
            hasResized: false
        };
    },
    computed: {
        headerSize() {
            return tailwindConfig.headerSize;
        },
        scrollPageIds() {
            // A flattened array of all the domIDs that can be scrolled to in the pagesModel.
            return _(appModel.pagesModel.pages).map((page) => {
                // Grabs the parent page id and all the subpage ids
                const subPages = _.map(page.pages, (subPage) => subPage.domId);
                return [page.domId, ...subPages];
            }).flatten().value();
        },
        ready() {
            if (!this.appModel.loaded || !this.appModel.regionModel.region) {
                return false;
            }
            // App is loaded and user or device has selected location
            return this.appModel.loaded && this.appModel.regionModel.region;
        },
        appModel() { return appModel; },
        // sidebarModel() { return appModel.sidebarModel; },
        // topPanelModel() { return appModel.topPanelModel; },
        // categoryModels() { return appModel.categoryModels; },

        dataStatus() { return dataService.status; }
    },
    watch: {
        dataStatus: {
            async handler(dataStatus) {
                if (dataStatus.loaded) {
                    appModel.initData(dataService.data);
                }
            },
            deep: true
        }
    },
    async updated() {
        // Wait for scrolly page to resize itself first
        if (!this.hasResized) {
            return;
        }

        // Calls syncScrolling only once, on first update, if the scrolling succeeds.
        if (!this.scrolled) {
            this.scrolled = await this.syncScrolling();
        }
    },
    mounted() {
        window.dha_app = this;
        if (!appModel.loaded && dataService.status.loaded) {
            // console.log('reinitialize app model with new data');
            appModel.initData(dataService.data);
        }
    },
    methods: {
        // If there is a hash, scrolls the window to the element with the hash id
        // Also sets up enter view to sync the current page as user scrolls.
        async syncScrolling() {
            const hash = window.location.hash;
            await this.$nextTick();
            const enterViewContainer = this.$refs.enterViewContainer;
            if (!enterViewContainer) {
                return false;
            }
            this.setupEnterView();
            if (!hash) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'auto'
                });
                return true;
            }
            const el = enterViewContainer.querySelector(hash);
            if (el) {
                const rect = el.getBoundingClientRect();
                window.scrollBy(0, rect.top);
            }
            return true;
        },
        setupEnterView() {
            // console.log(`setupEnterView on ${this.scrollPageIds}`);
            const idSelector = _.map(this.scrollPageIds, (id) => `#${id}`).join(', ');
            enterView({
                selector: idSelector,
                enter: (el) => {
                    // console.log('enter', el.getAttribute('id'), el.getBoundingClientRect().top, el.getBoundingClientRect().height);
                    appModel.pagesModel.setCurrentPageByDomId(el.getAttribute('id'));
                },
                exit: (el) => {
                    // console.log('exit', el.getAttribute('id'), el.offsetHeight);
                    const prevId = this.scrollPageIds[this.scrollPageIds.indexOf(el.getAttribute('id')) - 1];
                    appModel.pagesModel.setCurrentPageByDomId(prevId);
                },
                offset: 0.5,
            });
        },
        async onPageResize() {
            // The first time the scrolly page resizes itself, try to scroll to the hash
            if (!this.hasResized) {
                this.hasResized = true;

                if (!this.scrolled) {
                    this.scrolled = await this.syncScrolling();
                }
            }
        },
        closeModal() {
            document.body.style.overflowY = 'scroll';
        }
    }
};
</script>

<style scoped lang="postcss">
@import url("https://fonts.googleapis.com/css?family=Cabin:400,700&display=swap");

.app {
    @apply h-full w-full font-body flex flex-col;
    text-rendering: geometricPrecision;
}

.app-content {
    @apply relative flex flex-row mx-auto h-full bg-white w-full pb-8;
    max-width: 1400px;
}


.body-container {
    @apply h-full;
    width: calc(100% - 180pt);
}

@screen md {
    .body-container {
        width: calc(100% - 210pt);
    }
}

.scroll-container {
    @apply relative;
}

.data-loading {
  @apply absolute top-0 left-0;
}
@screen mobile {
    .body-container {
        width: 100%;
    }

    .scroll-container {
        max-width: 100vw;
        @apply mx-2;
    }
}
</style>
<style lang="postcss">
@screen mobile {
    html {
        scroll-behavior: auto;
    }
}
</style>
